import { useEffect, useState, type MouseEvent } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Menu,
  Stack,
  Tooltip,
} from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import Grid3x3Icon from '@mui/icons-material/Grid3x3';
import Grid4x4Icon from '@mui/icons-material/Grid4x4';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import {
  GetResourcesDocument,
  GetResourceDocument,
  CreateResourceDocument,
  UpdateResourceDocument,
  DeleteResourceDocument,
  ReorderResourcesDocument,
  type GetResourcesQuery,
  type ResourceFieldsFragment,
} from '@/generated/root-types';
import { useAuth } from '@/app/auth/AuthContext';
import { isSuperAdmin } from '@/utils/roles';
import { useNotification } from '@/app/contexts/NotificationContext';
import { DeleteConfirmDialog } from '@gastrosoft/shared/ui';
import { errorsFromPayload } from '@/utils/mutationErrors';

const MAX_FILE_BYTES = 10 * 1024 * 1024;
const GRID_SIZE_STORAGE_KEY = 'reseller_resources_grid_size';

const ALLOWED_MIME = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/pdf',
]);

type GridSize = 'compact' | 'comfortable';

type ResourceItem = ResourceFieldsFragment;

function isPdf(contentType: string): boolean {
  return contentType === 'application/pdf';
}

function isImage(contentType: string): boolean {
  return contentType.startsWith('image/');
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64.replace(/\s/g, ''));
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}

function triggerDownload(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.click();
  URL.revokeObjectURL(url);
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      if (typeof reader.result !== 'string') {
        reject(new Error('read failed'));
        return;
      }
      const comma = reader.result.indexOf(',');
      resolve(comma >= 0 ? reader.result.slice(comma + 1) : reader.result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(file);
  });
}

function gridColumns(size: GridSize) {
  if (size === 'compact') {
    return {
      xs: '1fr',
      sm: 'repeat(2, minmax(0, 1fr))',
      md: 'repeat(3, minmax(0, 1fr))',
      lg: 'repeat(4, minmax(0, 1fr))',
    };
  }
  return {
    xs: '1fr',
    sm: 'repeat(2, minmax(0, 1fr))',
    md: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  };
}

function loadGridSize(): GridSize {
  const stored = localStorage.getItem(GRID_SIZE_STORAGE_KEY);
  if (stored === 'compact') return 'compact';
  if (stored === 'comfortable' || stored === 'medium' || stored === 'large') return 'comfortable';
  return 'comfortable';
}

export default function Resources() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { showSuccess, showError } = useNotification();
  const superAdmin = isSuperAdmin(user);

  const [gridSize, setGridSize] = useState<GridSize>(loadGridSize);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuResource, setMenuResource] = useState<ResourceItem | null>(null);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [editTitle, setEditTitle] = useState('');
  const [editSortOrder, setEditSortOrder] = useState(0);
  const [editFile, setEditFile] = useState<File | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const { data, loading, error, refetch } = useQuery<GetResourcesQuery>(GetResourcesDocument);

  const [fetchResource, { loading: previewLoading, data: previewData }] = useLazyQuery(
    GetResourceDocument,
    { fetchPolicy: 'network-only' },
  );

  const [fetchResourceForDownload] = useLazyQuery(GetResourceDocument, {
    fetchPolicy: 'network-only',
  });

  const [createResource, { loading: creating }] = useMutation(CreateResourceDocument);
  const [updateResource, { loading: updating }] = useMutation(UpdateResourceDocument);
  const [deleteResource] = useMutation(DeleteResourceDocument);
  const [reorderResources] = useMutation(ReorderResourcesDocument);

  const resources = data?.resources ?? [];

  useEffect(() => {
    localStorage.setItem(GRID_SIZE_STORAGE_KEY, gridSize);
  }, [gridSize]);

  useEffect(() => {
    if (!previewId) {
      setPreviewBlobUrl(null);
      return;
    }
    void fetchResource({ variables: { id: previewId } });
  }, [previewId, fetchResource]);

  useEffect(() => {
    if (!previewId) {
      setPreviewBlobUrl(null);
      return;
    }

    const resource = previewData?.resource;
    if (resource?.id !== previewId || !resource.contentBase64 || !resource.contentType) {
      setPreviewBlobUrl(null);
      return;
    }

    const blob = base64ToBlob(resource.contentBase64, resource.contentType);
    const url = URL.createObjectURL(blob);
    setPreviewBlobUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [previewId, previewData]);

  const handleDownload = async (id: string, event?: MouseEvent) => {
    event?.stopPropagation();
    setDownloadingId(id);
    try {
      const result = await fetchResourceForDownload({ variables: { id } });
      const resource = result.data?.resource;
      if (!resource?.contentBase64) {
        showError(t('resources.loadFailed'));
        return;
      }
      triggerDownload(
        base64ToBlob(resource.contentBase64, resource.contentType),
        resource.fileName,
      );
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    } finally {
      setDownloadingId(null);
    }
  };

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_MIME.has(file.type)) return t('resources.invalidType');
    if (file.size > MAX_FILE_BYTES) return t('resources.fileTooLarge');
    return null;
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      showError(t('resources.fileRequired'));
      return;
    }
    const fileError = validateFile(uploadFile);
    if (fileError) {
      showError(fileError);
      return;
    }
    const title = uploadTitle.trim() || uploadFile.name.replace(/\.[^.]+$/, '');
    try {
      const contentBase64 = await readFileAsBase64(uploadFile);
      const res = await createResource({
        variables: {
          input: {
            title,
            fileName: uploadFile.name,
            contentType: uploadFile.type,
            contentBase64,
          },
        },
      });
      const err = errorsFromPayload(res.data?.createResource);
      if (err) {
        showError(err);
        return;
      }
      showSuccess(t('resources.created'));
      setUploadOpen(false);
      setUploadTitle('');
      setUploadFile(null);
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const openEdit = (item: ResourceItem) => {
    setMenuAnchor(null);
    setMenuResource(item);
    setEditTitle(item.title);
    setEditSortOrder(item.sortOrder);
    setEditFile(null);
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!menuResource) return;
    try {
      const input: {
        id: string;
        title: string;
        sortOrder: number;
        contentType?: string;
        contentBase64?: string;
        fileName?: string;
      } = {
        id: menuResource.id,
        title: editTitle.trim(),
        sortOrder: editSortOrder,
      };

      if (editFile) {
        const fileError = validateFile(editFile);
        if (fileError) {
          showError(fileError);
          return;
        }
        input.contentType = editFile.type;
        input.contentBase64 = await readFileAsBase64(editFile);
        input.fileName = editFile.name;
      }

      const res = await updateResource({ variables: { input } });
      const err = errorsFromPayload(res.data?.updateResource);
      if (err) {
        showError(err);
        return;
      }
      showSuccess(t('resources.updated'));
      setEditOpen(false);
      setMenuResource(null);
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const handleDelete = async () => {
    if (!menuResource) return;
    try {
      const res = await deleteResource({ variables: { input: { id: menuResource.id } } });
      const err = errorsFromPayload(res.data?.deleteResource);
      if (err) {
        showError(err);
        return;
      }
      showSuccess(t('resources.deleted'));
      setDeleteOpen(false);
      setMenuResource(null);
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
  };

  const moveResource = async (item: ResourceItem, direction: 'up' | 'down') => {
    const index = resources.findIndex((r) => r.id === item.id);
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= resources.length) return;

    const reordered = [...resources];
    [reordered[index], reordered[swapIndex]] = [reordered[swapIndex], reordered[index]];

    try {
      const res = await reorderResources({
        variables: {
          input: {
            items: reordered.map((r, i) => ({ id: r.id, sortOrder: i })),
          },
        },
      });
      const err = errorsFromPayload(res.data?.reorderResources);
      if (err) {
        showError(err);
        return;
      }
      await refetch();
    } catch (e) {
      showError(e instanceof Error ? e.message : t('errors.generic'));
    }
    setMenuAnchor(null);
  };

  const previewResource = previewData?.resource;
  const previewIsPdf = previewResource ? isPdf(previewResource.contentType) : false;
  const previewIsImage = previewResource ? isImage(previewResource.contentType) : false;

  return (
    <Box
      sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box', px: { xs: 1, sm: 2 }, py: 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          {t('resources.title')}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Tooltip title={t('resources.gridCompact')}>
              <IconButton
                size="small"
                color={gridSize === 'compact' ? 'primary' : 'default'}
                aria-label={t('resources.gridCompact')}
                aria-pressed={gridSize === 'compact'}
                onClick={() => setGridSize('compact')}
              >
                <Grid4x4Icon />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('resources.gridComfortable')}>
              <IconButton
                size="small"
                color={gridSize === 'comfortable' ? 'primary' : 'default'}
                aria-label={t('resources.gridComfortable')}
                aria-pressed={gridSize === 'comfortable'}
                onClick={() => setGridSize('comfortable')}
              >
                <Grid3x3Icon />
              </IconButton>
            </Tooltip>
          </Stack>
          {superAdmin ? (
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => setUploadOpen(true)}
            >
              {t('resources.upload')}
            </Button>
          ) : null}
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : null}

      {error ? (
        <Typography color="error" sx={{ mb: 2 }}>
          {error.message}
        </Typography>
      ) : null}

      {!loading && resources.length === 0 ? (
        <Typography color="text.secondary">{t('resources.empty')}</Typography>
      ) : null}

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns(gridSize),
          gap: 2,
        }}
      >
        {resources.map((item) => (
          <Card
            key={item.id}
            variant="outlined"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              cursor: 'pointer',
              '&:hover': { borderColor: 'primary.main' },
            }}
            onClick={() => setPreviewId(item.id)}
          >
            <Box
              sx={{
                height: 160,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'action.hover',
                overflow: 'hidden',
              }}
            >
              {isImage(item.contentType) && item.previewBase64 ? (
                <Box
                  component="img"
                  src={item.previewBase64}
                  alt={item.title}
                  sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
                />
              ) : (
                <Stack alignItems="center" spacing={0.5} color="text.secondary">
                  <PictureAsPdfIcon sx={{ fontSize: 56 }} />
                  <Typography variant="caption">{t('resources.pdfDocument')}</Typography>
                </Stack>
              )}
            </Box>
            <CardContent sx={{ flex: 1, pb: 1 }}>
              <Typography variant="subtitle1" fontWeight={600} noWrap title={item.title}>
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary" noWrap title={item.fileName}>
                {item.fileName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatFileSize(item.fileSizeBytes)}
              </Typography>
            </CardContent>
            <CardActions sx={{ justifyContent: 'space-between', pt: 0 }}>
              <Button
                size="small"
                startIcon={
                  downloadingId === item.id ? <CircularProgress size={16} /> : <DownloadIcon />
                }
                onClick={(e) => void handleDownload(item.id, e)}
                disabled={downloadingId === item.id}
              >
                {t('resources.download')}
              </Button>
              {superAdmin ? (
                <IconButton
                  size="small"
                  aria-label={t('resources.actions')}
                  onClick={(e) => {
                    e.stopPropagation();
                    setMenuResource(item);
                    setMenuAnchor(e.currentTarget);
                  }}
                >
                  <MoreVertIcon />
                </IconButton>
              ) : null}
            </CardActions>
          </Card>
        ))}
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            if (menuResource) openEdit(menuResource);
          }}
        >
          {t('resources.edit')}
        </MenuItem>
        <MenuItem
          disabled={!menuResource || resources.findIndex((r) => r.id === menuResource.id) === 0}
          onClick={() => menuResource && void moveResource(menuResource, 'up')}
        >
          <ArrowUpwardIcon fontSize="small" sx={{ mr: 1 }} />
          {t('resources.moveUp')}
        </MenuItem>
        <MenuItem
          disabled={
            !menuResource ||
            resources.findIndex((r) => r.id === menuResource.id) === resources.length - 1
          }
          onClick={() => menuResource && void moveResource(menuResource, 'down')}
        >
          <ArrowDownwardIcon fontSize="small" sx={{ mr: 1 }} />
          {t('resources.moveDown')}
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setDeleteOpen(true);
          }}
          sx={{ color: 'error.main' }}
        >
          {t('resources.delete')}
        </MenuItem>
      </Menu>

      <Dialog
        open={previewId !== null}
        onClose={() => setPreviewId(null)}
        fullScreen
        PaperProps={{ sx: { bgcolor: 'background.default' } }}
      >
        <DialogTitle
          sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
        >
          <Typography variant="h6" component="span" noWrap sx={{ pr: 2 }}>
            {previewResource?.title ?? t('resources.preview')}
          </Typography>
          <Stack direction="row" spacing={1}>
            {previewResource ? (
              <Button
                startIcon={<DownloadIcon />}
                onClick={() => void handleDownload(previewResource.id)}
              >
                {t('resources.download')}
              </Button>
            ) : null}
            <IconButton onClick={() => setPreviewId(null)} aria-label={t('common.back')}>
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 1, sm: 2 },
          }}
        >
          {previewLoading || !previewBlobUrl ? (
            <CircularProgress />
          ) : previewIsImage ? (
            <Box
              component="img"
              src={previewBlobUrl}
              alt={previewResource?.title ?? ''}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : previewIsPdf ? (
            <Box
              component="iframe"
              src={previewBlobUrl}
              title={previewResource?.title ?? t('resources.pdfDocument')}
              sx={{ width: '100%', height: '100%', minHeight: '70vh', border: 0 }}
            />
          ) : (
            <Typography color="text.secondary">{t('resources.unsupportedPreview')}</Typography>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={uploadOpen} onClose={() => setUploadOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('resources.upload')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('resources.titleLabel')}
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              fullWidth
              helperText={t('resources.titleOptional')}
            />
            <Button variant="outlined" component="label">
              {uploadFile ? uploadFile.name : t('resources.chooseFile')}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                onChange={(e) => setUploadFile(e.target.files?.[0] ?? null)}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={() => void handleUpload()} disabled={creating}>
            {t('resources.upload')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('resources.edit')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label={t('resources.titleLabel')}
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label={t('resources.sortOrder')}
              type="number"
              value={editSortOrder}
              onChange={(e) => setEditSortOrder(Number(e.target.value))}
              fullWidth
            />
            <Button variant="outlined" component="label">
              {editFile ? editFile.name : t('resources.replaceFile')}
              <input
                type="file"
                hidden
                accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
                onChange={(e) => setEditFile(e.target.files?.[0] ?? null)}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={() => void handleEdit()} disabled={updating}>
            {t('common.save')}
          </Button>
        </DialogActions>
      </Dialog>

      <DeleteConfirmDialog
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => void handleDelete()}
        title={t('resources.deleteTitle')}
        message={t('resources.deleteConfirm', { title: menuResource?.title ?? '' })}
      />
    </Box>
  );
}
